// @mui
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
// router
import { Outlet } from "react-router-dom";
import { useResponsive } from "hooks/useResponsive";
import { useWindowDimensions } from "hooks/useWindowDimensions";
import { Box, Paper, styled, alpha } from "@mui/material";

interface StyledRootProps {
  screenHeight: number;
}

const StyledRoot = styled(Box, {
  shouldForwardProp: (prop: PropertyKey) => prop !== "screenHeight",
})<StyledRootProps>(({ screenHeight, theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
  minHeight: "100vh",
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(145deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.dark, 0.12)} 50%, ${alpha(theme.palette.secondary.dark, 0.08)} 100%)`
      : `linear-gradient(145deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.light, 0.06)} 50%, ${alpha(theme.palette.secondary.light, 0.04)} 100%)`,
}));

interface StyledOutletProps {
  collapseClick?: boolean;
  withNavbar?: boolean;
  screenHeight: number;
}

const StyledOutlet = styled("main", {
  shouldForwardProp: (prop) =>
    prop !== "collapseClick" &&
    prop !== "withNavbar" &&
    prop !== "screenHeight",
})<StyledOutletProps>(({ withNavbar, theme, screenHeight }) => ({
  flexGrow: 1,
  padding: theme.spacing(2),
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: "100%",
  height: "100%",
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(3),
  backgroundColor: "transparent",
  boxShadow: "none",
  overflow: "hidden",
}));

export const AuthLayout = () => {
  const isDesktop = useResponsive("up", "lg");
  const { height } = useWindowDimensions();
  return (
    <StyledRoot screenHeight={height}>
      <StyledOutlet screenHeight={height}>
        <StyledPaper>
          <Container maxWidth={"xl"} sx={{ height: "100vh" }}>
            <Grid container spacing={3} sx={{ height: "100%" }}>
              <Grid
                item
                xs={12}
                md={isDesktop ? 6 : 12}
                sx={{ height: isDesktop ? "100%" : "50%" }}
              >
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Outlet />
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                sx={{ height: isDesktop ? "100%" : "50%" }}
              >
                <Stack
                  alignItems={"center"}
                  justifyContent={"center"}
                  sx={{
                    height: isDesktop ? "100%" : "fit-content",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: isDesktop ? 500 : "100%",
                      borderRadius: "24px",
                      overflow: "hidden",
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "0 32px 64px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)"
                          : "0 32px 64px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.03)",
                    }}
                  >
                    <img
                      src={"/assets/images/Login-background.jpg"}
                      alt={"Login background"}
                      style={{
                        width: "100%",
                        maxHeight: isDesktop ? "auto" : "200px",
                        height: "auto",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </StyledPaper>
      </StyledOutlet>
    </StyledRoot>
  );
};
